import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Crosshair, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const brand = useBranding();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(loc.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cd-marketplace" style={{ minHeight: "100vh", display: "flex", background: "var(--cd-white)", fontFamily: "var(--cd-font)" }} data-testid="login-page">
      {/* Left panel — navy brand */}
      <div
        style={{
          flex: 1,
          background: "linear-gradient(135deg, var(--cd-navy-950), #071d40)",
          color: "#fff",
          padding: "56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{ position: "absolute", inset: "auto -60px -80px auto", width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, rgba(62,232,211,.22), transparent 60%)", filter: "blur(20px)" }}
        />
        <div style={{ position: "relative" }}>
          {brand.brands_logo_url && (
            <img
              src={brand.brands_logo_url}
              alt="CasperBrands&Co"
              data-testid="brands-logo"
              style={{ height: 86, width: "auto", display: "block" }}
            />
          )}
          <div
            data-testid="brand-tagline"
            style={{
              marginTop: brand.brands_logo_url ? 14 : 0, fontSize: 14,
              letterSpacing: ".04em", color: "#ffffff", fontWeight: 500,
            }}
          >
            a CasperBrands&amp;Co company
          </div>
        </div>

        <div style={{ position: "relative", maxWidth: 520 }}>
          <span className="cd-eyebrow" style={{ color: "var(--cd-cyan-400)" }}>
            {brand.login_overline || "Domain trading desk"}
          </span>
          <h1
            className="cd-display"
            data-testid="login-headline"
            style={{ color: "#fff", marginTop: 14 }}
          >
            {brand.login_headline || "Own the domains that own the market."}
          </h1>
        </div>

        <div style={{ position: "relative", fontSize: 12, color: "rgba(255,255,255,.5)" }}>
          © {new Date().getFullYear()} {brand.brand_name}
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 40 }}>
        <form
          onSubmit={submit}
          autoComplete="off"
          style={{ width: "100%", maxWidth: 420 }}
        >
          <img
            src={brand.logo_url || "/brand/casperdomains-logo.png"}
            alt={brand.brand_name || "CasperDomains"}
            data-testid="brand-logo"
            style={{ height: 56, width: "auto", display: "block", margin: "0 auto 28px" }}
          />
          <span className="cd-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
            <Lock size={12} strokeWidth={2} /> Admin sign in
          </span>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--cd-gray-700)", marginBottom: 6 }}>Email</label>
          <input
            id="email" type="email" required data-testid="login-email"
            name="cd-signin-email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="off" autoCorrect="off" spellCheck={false}
            autoFocus className="cd-input"
          />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--cd-gray-700)", marginBottom: 6, marginTop: 16 }}>Password</label>
          <input
            id="password" type="password" required data-testid="login-password"
            name="cd-signin-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="cd-input"
          />

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="cd-btn cd-btn--primary cd-btn--full"
            style={{ marginTop: 24 }}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

        </form>
      </div>
    </div>
  );
}
